import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  NotificationInboxResponse,
  UnreadCountResponse,
  LoginAnnouncementResponse,
} from '@/lib/api/notifications';
import { NotificationsAPI } from '@/lib/api/notifications';
import { queryKeys } from '@/lib/query/client';

export function useNotificationInbox(enabled = true) {
  return useQuery({
    queryKey: queryKeys.notificationInbox(),
    queryFn: () => NotificationsAPI.getInbox(),
    enabled,
    staleTime: 0,
  });
}

export function useNotificationUnreadCount(enabled = true) {
  return useQuery({
    queryKey: queryKeys.notificationUnreadCount(),
    queryFn: () => NotificationsAPI.getUnreadCount(),
    refetchInterval: 60_000,
    enabled,
  });
}

export function useLoginAnnouncements() {
  return useQuery({
    queryKey: queryKeys.notificationLoginAnnouncements(),
    queryFn: () => NotificationsAPI.getLoginAnnouncements(),
    gcTime: 0,
    staleTime: 0,
  });
}

function invalidateAllNotifications(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => NotificationsAPI.markRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications() });

      const prevInbox = queryClient.getQueryData<NotificationInboxResponse>(
        queryKeys.notificationInbox(),
      );
      const prevCount = queryClient.getQueryData<UnreadCountResponse>(
        queryKeys.notificationUnreadCount(),
      );

      if (prevInbox) {
        queryClient.setQueryData<NotificationInboxResponse>(queryKeys.notificationInbox(), {
          ...prevInbox,
          unreadCount: Math.max(0, prevInbox.unreadCount - 1),
          items: prevInbox.items.map((item) =>
            item.id === notificationId ? { ...item, isRead: true } : item,
          ),
        });
      }
      if (prevCount) {
        queryClient.setQueryData<UnreadCountResponse>(queryKeys.notificationUnreadCount(), {
          unreadCount: Math.max(0, prevCount.unreadCount - 1),
        });
      }

      return { prevInbox, prevCount };
    },
    onError: (_err, _id, context) => {
      if (context?.prevInbox) {
        queryClient.setQueryData(queryKeys.notificationInbox(), context.prevInbox);
      }
      if (context?.prevCount) {
        queryClient.setQueryData(queryKeys.notificationUnreadCount(), context.prevCount);
      }
    },
    onSettled: () => invalidateAllNotifications(queryClient),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => NotificationsAPI.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications() });

      const prevInbox = queryClient.getQueryData<NotificationInboxResponse>(
        queryKeys.notificationInbox(),
      );
      const prevCount = queryClient.getQueryData<UnreadCountResponse>(
        queryKeys.notificationUnreadCount(),
      );

      if (prevInbox) {
        queryClient.setQueryData<NotificationInboxResponse>(queryKeys.notificationInbox(), {
          ...prevInbox,
          unreadCount: 0,
          items: prevInbox.items.map((item) => ({ ...item, isRead: true })),
        });
      }
      queryClient.setQueryData<UnreadCountResponse>(queryKeys.notificationUnreadCount(), {
        unreadCount: 0,
      });

      return { prevInbox, prevCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevInbox) {
        queryClient.setQueryData(queryKeys.notificationInbox(), context.prevInbox);
      }
      if (context?.prevCount) {
        queryClient.setQueryData(queryKeys.notificationUnreadCount(), context.prevCount);
      }
    },
    onSettled: () => invalidateAllNotifications(queryClient),
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => NotificationsAPI.dismiss(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications() });

      const prevInbox = queryClient.getQueryData<NotificationInboxResponse>(
        queryKeys.notificationInbox(),
      );
      const prevCount = queryClient.getQueryData<UnreadCountResponse>(
        queryKeys.notificationUnreadCount(),
      );
      const prevAnnouncements = queryClient.getQueryData<LoginAnnouncementResponse>(
        queryKeys.notificationLoginAnnouncements(),
      );

      if (prevInbox) {
        const dismissed = prevInbox.items.find((i) => i.id === notificationId);
        const wasUnread = dismissed && !dismissed.isRead;
        queryClient.setQueryData<NotificationInboxResponse>(queryKeys.notificationInbox(), {
          ...prevInbox,
          unreadCount: wasUnread ? Math.max(0, prevInbox.unreadCount - 1) : prevInbox.unreadCount,
          items: prevInbox.items.filter((item) => item.id !== notificationId),
        });
        if (wasUnread && prevCount) {
          queryClient.setQueryData<UnreadCountResponse>(queryKeys.notificationUnreadCount(), {
            unreadCount: Math.max(0, prevCount.unreadCount - 1),
          });
        }
      }
      if (prevAnnouncements) {
        queryClient.setQueryData<LoginAnnouncementResponse>(
          queryKeys.notificationLoginAnnouncements(),
          {
            announcements: prevAnnouncements.announcements.filter((a) => a.id !== notificationId),
          },
        );
      }

      return { prevInbox, prevCount, prevAnnouncements };
    },
    onError: (_err, _id, context) => {
      if (context?.prevInbox) {
        queryClient.setQueryData(queryKeys.notificationInbox(), context.prevInbox);
      }
      if (context?.prevCount) {
        queryClient.setQueryData(queryKeys.notificationUnreadCount(), context.prevCount);
      }
      if (context?.prevAnnouncements) {
        queryClient.setQueryData(
          queryKeys.notificationLoginAnnouncements(),
          context.prevAnnouncements,
        );
      }
    },
    onSettled: () => invalidateAllNotifications(queryClient),
  });
}
